import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  ArrowForward,
  ArrowBack,
  Image as ImageIcon,
} from '@mui/icons-material';
import fileService, { FileInfo, CompositeFormat } from '../services/fileService';
import { useNotification } from '../contexts/NotificationContext';

interface BeforeAfterComposerProps {
  open: boolean;
  onClose: () => void;
  photoApres: FileInfo;        // La photo APRÈS (vient d'être uploadée)
  photoApresPreview: string;   // URL de prévisualisation
  availablePhotos: FileInfo[]; // Photos disponibles pour sélectionner le AVANT
  photoPreviews: Map<number, string>; // Map des prévisualisations
  projetId: number;            // ID du projet (pour l'API composite)
  onCompositeCreated?: (composite: FileInfo) => void;
}

const ZONES_CHANTIER = [
  'Cuisine',
  'Salle de bain',
  'Salon',
  'Chambre',
  'Salle à manger',
  'Entrée',
  'Couloir',
  'Bureau',
  'Garage',
  'Extérieur',
  'Autre',
];

/**
 * Composant de création de compositions avant/après
 *
 * Workflow en 3 étapes :
 * 1. Sélection de la photo AVANT
 * 2. Configuration (format, zone, texte)
 * 3. Génération et prévisualisation
 */
const BeforeAfterComposer: React.FC<BeforeAfterComposerProps> = ({
  open,
  onClose,
  photoApres,
  photoApresPreview,
  availablePhotos,
  photoPreviews,
  projetId,
  onCompositeCreated,
}) => {
  const { showSuccess, showError } = useNotification();

  // États du workflow
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPhotoAvant, setSelectedPhotoAvant] = useState<FileInfo | null>(null);
  const [generating, setGenerating] = useState(false);

  // Configuration
  const [format, setFormat] = useState<CompositeFormat>('side-by-side');
  const [zone, setZone] = useState('');
  const [customZone, setCustomZone] = useState('');
  const [description, setDescription] = useState('');
  const [includeContact, setIncludeContact] = useState(true);

  const steps = ['Sélectionner AVANT', 'Configuration', 'Génération'];

  const handleNext = () => {
    if (activeStep === 0 && !selectedPhotoAvant) {
      showError('Veuillez sélectionner une photo AVANT');
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSelectPhotoAvant = (photo: FileInfo) => {
    setSelectedPhotoAvant(photo);
  };

  const handleGenerate = async () => {
    if (!selectedPhotoAvant) {
      showError('Aucune photo AVANT sélectionnée');
      return;
    }

    setGenerating(true);

    try {
      const finalZone = zone === 'Autre' ? customZone : zone;

      const composite = await fileService.generateBeforeAfterComposite(
        selectedPhotoAvant.id,
        photoApres.id,
        {
          format,
          zone: finalZone || undefined,
          description: description || undefined,
          includeContact,
        },
        projetId
      );

      showSuccess('Image composite créée avec succès !');

      if (onCompositeCreated) {
        onCompositeCreated(composite);
      }

      // Réinitialiser et fermer
      handleClose();
    } catch (error: any) {
      console.error('Erreur génération composite:', error);
      showError(error.message || 'Erreur lors de la génération de l\'image composite');
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setSelectedPhotoAvant(null);
    setFormat('side-by-side');
    setZone('');
    setCustomZone('');
    setDescription('');
    setIncludeContact(true);
    onClose();
  };

  const formatLabels: Record<CompositeFormat, string> = {
    'side-by-side': 'Côte-à-côte (classique)',
    'vertical': 'Vertical (Instagram)',
    'arrow': 'Avec flèche centrale',
  };

  const formatDescriptions: Record<CompositeFormat, string> = {
    'side-by-side': 'Format standard, comparaison directe',
    'vertical': 'Format story, idéal pour mobile',
    'arrow': 'Effet visuel marketing avec flèche',
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Créer une comparaison avant/après</Typography>
          <Button onClick={handleClose} startIcon={<Close />} size="small">
            Fermer
          </Button>
        </Box>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 3, py: 2 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent>
        {/* Étape 1 : Sélection photo AVANT */}
        {activeStep === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Sélectionnez la photo <strong>AVANT travaux</strong> à associer avec votre photo APRÈS
            </Alert>

            {availablePhotos.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <ImageIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Aucune photo disponible pour ce devis
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Uploadez d'abord une photo AVANT pour créer une comparaison
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {availablePhotos.map((photo) => {
                  const preview = photoPreviews.get(photo.id);
                  const isSelected = selectedPhotoAvant?.id === photo.id;

                  return (
                    <Grid item xs={6} sm={4} key={photo.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: isSelected ? '3px solid #F59E0B' : '1px solid #e0e0e0',
                          position: 'relative',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'scale(1.05)',
                            boxShadow: 3,
                          },
                        }}
                        onClick={() => handleSelectPhotoAvant(photo)}
                      >
                        {isSelected && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 1,
                            }}
                          >
                            <CheckCircle sx={{ color: '#F59E0B', fontSize: 32 }} />
                          </Box>
                        )}
                        <CardMedia
                          component="img"
                          height="150"
                          image={preview || '/placeholder.png'}
                          alt={photo.nom}
                          sx={{ objectFit: 'cover' }}
                        />
                        <CardContent sx={{ py: 1 }}>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {photo.nom}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {new Date(photo.dateUpload).toLocaleDateString('fr-FR')}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

        {/* Étape 2 : Configuration */}
        {activeStep === 1 && (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Personnalisez votre composition avant/après
            </Alert>

            {/* Prévisualisation des photos sélectionnées */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  AVANT
                </Typography>
                <img
                  src={photoPreviews.get(selectedPhotoAvant?.id || 0)}
                  alt="Avant"
                  style={{
                    width: '150px',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ArrowForward sx={{ fontSize: 40, color: '#F59E0B' }} />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  APRÈS
                </Typography>
                <img
                  src={photoApresPreview}
                  alt="Après"
                  style={{
                    width: '150px',
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                  }}
                />
              </Box>
            </Box>

            {/* Format de composition */}
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">Format de composition</FormLabel>
              <RadioGroup
                value={format}
                onChange={(e) => setFormat(e.target.value as CompositeFormat)}
              >
                {(Object.keys(formatLabels) as CompositeFormat[]).map((fmt) => (
                  <Box key={fmt} sx={{ mb: 1 }}>
                    <FormControlLabel
                      value={fmt}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body1">{formatLabels[fmt]}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDescriptions[fmt]}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </RadioGroup>
            </FormControl>

            {/* Zone du chantier */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Zone du chantier (optionnel)</InputLabel>
              <Select
                value={zone}
                onChange={(e) => setZone(e.target.value)}
                label="Zone du chantier (optionnel)"
              >
                <MenuItem value="">
                  <em>Aucune</em>
                </MenuItem>
                {ZONES_CHANTIER.map((z) => (
                  <MenuItem key={z} value={z}>
                    {z}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {zone === 'Autre' && (
              <TextField
                fullWidth
                label="Nom de la zone"
                value={customZone}
                onChange={(e) => setCustomZone(e.target.value)}
                sx={{ mb: 2 }}
                placeholder="Ex: Véranda, Cave, etc."
              />
            )}

            {/* Description */}
            <TextField
              fullWidth
              label="Description (optionnel)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
              placeholder="Ex: Rénovation complète, Pose de carrelage..."
            />

            {/* Inclure coordonnées */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeContact}
                  onChange={(e) => setIncludeContact(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Inclure mes coordonnées</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Nom, téléphone et email seront ajoutés sur l'image
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        {/* Étape 3 : Génération */}
        {activeStep === 2 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {generating ? (
              <Box>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Génération en cours...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Création de votre image composite avant/après
                </Typography>
              </Box>
            ) : (
              <Box>
                <CheckCircle sx={{ fontSize: 80, color: '#4CAF50', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Prêt à générer !
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Récapitulatif de votre composition :
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
                  <Chip label={formatLabels[format]} color="primary" />
                  {zone && <Chip label={zone === 'Autre' ? customZone : zone} />}
                  {includeContact && <Chip label="Avec coordonnées" />}
                </Box>

                <Alert severity="info">
                  L'image composite sera automatiquement enregistrée dans ce devis
                </Alert>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={generating}>
          Annuler
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={generating} startIcon={<ArrowBack />}>
            Retour
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={activeStep === 0 && !selectedPhotoAvant}
            endIcon={<ArrowForward />}
          >
            Suivant
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {generating ? 'Génération...' : 'Générer'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BeforeAfterComposer;
